// Basemap tile providers offered in "Basic" render mode. All are usable
// for commercial purposes with attribution; CartoDB layers are CC-BY 3.0,
// ESRI World Imagery is included with its required credit line, and
// Thunderforest's free tier explicitly permits commercial use up to
// 150k tiles/month (we soft-cap server-side below that — see
// backend/tile_basemap.py PROVIDER_CAPS).
//
// Backend rendering uses the same `id` to pick the right provider when
// stitching tiles, so keep ids stable — they're persisted in saved
// generation settings.
//
// Thunderforest's policy requires attribution with working links to
// www.thunderforest.com and www.openstreetmap.org/copyright; the
// attribution string below carries them as `<a>` tags so Leaflet renders
// the standard clickable credit on the live preview. The printed PNG
// uses the same plain-text version (links not clickable on paper).
const TF_KEY = process.env.REACT_APP_THUNDERFOREST_API_KEY;
const MB_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const BUNDLED_STYLES = [
  {
    id: 'positron_nolabels',
    label: 'Positron',
    sublabel: 'Light, no labels',
    url: 'https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors © CARTO',
    subdomains: 'abcd',
    maxZoom: 19,
  },
  {
    id: 'positron',
    label: 'Positron',
    sublabel: 'Light, with labels',
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors © CARTO',
    subdomains: 'abcd',
    maxZoom: 19,
  },
  {
    id: 'voyager_nolabels',
    label: 'Voyager',
    sublabel: 'Mid-tone, no labels',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager_nolabels/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors © CARTO',
    subdomains: 'abcd',
    maxZoom: 19,
  },
  {
    id: 'voyager',
    label: 'Voyager',
    sublabel: 'Mid-tone, with labels',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors © CARTO',
    subdomains: 'abcd',
    maxZoom: 19,
  },
  {
    id: 'darkmatter_nolabels',
    label: 'Dark Matter',
    sublabel: 'Dark, no labels',
    url: 'https://{s}.basemaps.cartocdn.com/dark_nolabels/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors © CARTO',
    subdomains: 'abcd',
    maxZoom: 19,
  },
  {
    id: 'darkmatter',
    label: 'Dark Matter',
    sublabel: 'Dark, with labels',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
    attribution: '© OpenStreetMap contributors © CARTO',
    subdomains: 'abcd',
    maxZoom: 19,
  },
  {
    id: 'esri_imagery',
    label: 'Satellite',
    sublabel: 'Aerial imagery',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '© Esri — Source: Esri, Maxar, Earthstar Geographics',
    maxZoom: 19,
  },
];

// Thunderforest entries are registered only when an API key is configured
// at build time. Without one the URLs would 401 and the cards would all
// render blank — better to hide them entirely. api.thunderforest.com is
// the canonical host; subdomain sharding only matters on HTTP/1.1 and
// most browsers negotiate HTTP/2 with Thunderforest anyway.
const TF_ATTRIBUTION =
  'Maps © <a href="https://www.thunderforest.com/" target="_blank" rel="noreferrer">Thunderforest</a>, Data © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap contributors</a>';

const tfStyle = (id, label, sublabel, slug) => ({
  id,
  label,
  sublabel,
  url: `https://api.thunderforest.com/${slug}/{z}/{x}/{y}.png?apikey=${TF_KEY}`,
  attribution: TF_ATTRIBUTION,
  maxZoom: 22,
});

const THUNDERFOREST_STYLES = TF_KEY
  ? [
    // tfStyle('thunderforest_cycle', 'Cycle', 'Cycling routes', 'cycle'),
    tfStyle('thunderforest_transport', 'Transport', 'Public transit', 'transport'),
    tfStyle('thunderforest_landscape', 'Landscape', 'Topographic', 'landscape'),
    tfStyle('thunderforest_outdoors', 'Outdoors', 'Trails & terrain', 'outdoors'),
    // tfStyle('thunderforest_transport_dark', 'Transport Dark', 'Dark with transit', 'transport-dark'),
    // tfStyle('thunderforest_spinal_map', 'Spinal Map', 'Bold black & red', 'spinal-map'),
    tfStyle('thunderforest_pioneer', 'Pioneer', 'Vintage', 'pioneer'),
    tfStyle('thunderforest_mobile_atlas', 'Mobile Atlas', 'Mobile-friendly atlas', 'mobile-atlas'),
    tfStyle('thunderforest_neighbourhood', 'Neighbourhood', 'Urban detail', 'neighbourhood'),
    tfStyle('thunderforest_atlas', 'Atlas', 'Classic atlas', 'atlas'),
  ]
  : [];

// Mapbox attribution must include the "Improve this map" link when using
// Mapbox-derived styles (streets/light/dark/satellite/etc.) per
// https://docs.mapbox.com/help/getting-started/attribution/. All three
// links open in a new tab.
const MB_ATTRIBUTION =
  '© <a href="https://www.mapbox.com/about/maps/" target="_blank" rel="noreferrer">Mapbox</a> © <a href="https://www.openstreetmap.org/copyright/" target="_blank" rel="noreferrer">OpenStreetMap</a> <a href="https://www.mapbox.com/map-feedback/" target="_blank" rel="noreferrer">Improve this map</a>';

const mbStyle = (id, label, sublabel, slug) => ({
  id,
  label,
  sublabel,
  url: `https://api.mapbox.com/styles/v1/mapbox/${slug}/tiles/256/{z}/{x}/{y}?access_token=${MB_TOKEN}`,
  attribution: MB_ATTRIBUTION,
  maxZoom: 22,
});

const MAPBOX_STYLES = MB_TOKEN
  ? [
    mbStyle('mapbox_streets', 'Streets', 'Standard street map', 'streets-v12'),
    mbStyle('mapbox_outdoors', 'Outdoors', 'Hillshading & contours', 'outdoors-v12'),
    mbStyle('mapbox_light', 'Light', 'Minimal light', 'light-v11'),
    mbStyle('mapbox_dark', 'Dark', 'Minimal dark', 'dark-v11'),
    mbStyle('mapbox_satellite', 'Satellite', 'Aerial imagery', 'satellite-v9'),
    mbStyle('mapbox_satellite_streets', 'Satellite Streets', 'Aerial with labels', 'satellite-streets-v12'),
    // mbStyle('mapbox_navigation_day', 'Navigation Day', 'Aerial with labels', 'navigation-day-v1'),
  ]
  : [];

export const BASIC_STYLES = [...BUNDLED_STYLES, ...THUNDERFOREST_STYLES, ...MAPBOX_STYLES];

export const DEFAULT_BASIC_STYLE_ID = 'positron_nolabels';

export const getBasicStyle = (id) =>
  BASIC_STYLES.find((s) => s.id === id) || BASIC_STYLES[0];

// Threshold above which "Fully customizable" mode is unavailable and the
// user must use a Basic style. Mirrors the backend rule, so keep both in
// sync if we ever change it.
export const BASIC_ONLY_KM2_THRESHOLD = 50000;

// Display formatting for km² values shown next to the slider/input.
// Ranges:
//   1 - 999      → "<n> km²"
//   1000 - 999999 → "<n>k km²"  (one decimal when not exactly round)
//   >= 1000000  → "<n>m km²"
export const formatKm2 = (km2) => {
  const n = Number(km2);
  if (!Number.isFinite(n) || n < 0) return `${km2} km²`;
  if (n < 1000) return `${Math.round(n)} km²`;
  if (n < 1_000_000) {
    const k = n / 1000;
    const txt = k >= 100 || Number.isInteger(k)
      ? `${Math.round(k)}`
      : k.toFixed(1).replace(/\.0$/, '');
    return `${txt}k km²`;
  }
  const m = n / 1_000_000;
  const txt = m >= 100 || Number.isInteger(m)
    ? `${Math.round(m)}`
    : m.toFixed(1).replace(/\.0$/, '');
  return `${txt}m km²`;
};
